/** ADVANCED_VIDEO_GENERATION_CARD — Shot Reference Assets (VIDEO_SHOT_
 * REFERENCE_ASSETS_P0_V1). Structured character / product / scene /
 * other image refs that drive keyframe identity.
 *
 * `variant="inline"` (default for keyframe zone): always expanded.
 * `variant="summary"`: legacy collapsible tray (optional footer summary).
 */

import { CloseOutlined, DownOutlined, PictureOutlined, PlusOutlined, RightOutlined, UserOutlined } from '@ant-design/icons'
import { type DragEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveCanvasImageHoverPreviewUrl, resolveCanvasImageThumbUrl } from '../canvasImageQuality'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'
import { videoStructuredAssetHandleId } from '../dataflow'
import type { InputAssetCategory, InputAssetRef } from '../videoKeyframeTimeline'
import VideoInputSectionHandle from './VideoInputSectionHandle'
import { ASSET_MENTION_DND_TYPE, type AssetMentionDragPayload } from './InputMaterialsRow'
import { vistoeraDndProps } from '../canvasDragContract'

/** VistoeraTASK-00064 P1/P3 — a structured asset card's OWN Asset
 * Mention identity key. Edge-derived cards carry a synthetic
 * `edge:<edgeId>` display id (see `VideoInputAssetDisplayRef` doc
 * below); the underlying Asset Mention marker must embed the BARE
 * `edgeId` (never the `edge:` prefix) so it round-trips through the
 * EXACT SAME `onDisconnectMaterial` → `stripMentionsForEdge(text,
 * edgeId)` cleanup path every other edge-derived reference chip already
 * uses (`GenerateVideoNode.tsx`'s `handleRemoveInputAssetRef`) — never a
 * second, parallel stripping convention. A picker-added manual ref has
 * no such prefix and uses its own stable `id` verbatim. */
export function structuredAssetMentionKey(refId: string): string {
  return refId.startsWith('edge:') ? refId.slice('edge:'.length) : refId
}

/** VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
 * §P15 — a display-only superset of the persisted `InputAssetRef` shape
 * that also covers an EDGE-derived structured-asset slot
 * (`character-in`/`product-in`/`scene-in`/`reference-asset-in`
 * `KeyframeSlotMaterial`, `dataflow.ts`), merged into the SAME list the
 * pre-existing picker-added refs already render in (never a second,
 * parallel UI). `locked` edge-derived cards have no "replace" affordance
 * (there is no field to replace — the asset comes from the connected
 * node; removing means deleting the EDGE, via `onRemove`'s `edge:`-
 * prefixed id branch in `GenerateVideoNode.tsx`) and use
 * `generatedOutputId`/`nodeType` (a `generate_image` gen-to-gen source)
 * for thumbnail resolution when `assetId` (a `image_asset` SiteAsset id)
 * is absent. */
export interface VideoInputAssetDisplayRef extends InputAssetRef {
  generatedOutputId?: string | null
  sourceNodeType?: string | null
  locked?: boolean
  /** Edge-derived-only: false while a `generate_image` source hasn't
   * finished yet (spec §P15 — edge exists, asset not materialized). */
  ready?: boolean
}

const CATEGORY_ORDER: InputAssetCategory[] = ['character', 'object', 'scene', 'reference']
const CATEGORY_LABEL_KEY: Record<InputAssetCategory, string> = {
  character: 'generation.inputAssetCategoryCharacter',
  scene: 'generation.inputAssetCategoryScene',
  object: 'generation.inputAssetCategoryObject',
  reference: 'generation.inputAssetCategoryReference',
}
const CATEGORY_SHORT_LABEL_KEY: Record<InputAssetCategory, string> = {
  character: 'generation.inputAssetShortCharacter',
  scene: 'generation.inputAssetShortScene',
  object: 'generation.inputAssetShortObject',
  reference: 'generation.inputAssetShortReference',
}

interface Props {
  workspaceId: string
  refs: VideoInputAssetDisplayRef[]
  disabled?: boolean
  /** inline = always open inside keyframe zone; summary = collapsible */
  variant?: 'inline' | 'summary'
  onRemove: (id: string) => void
  /** Opens Image Asset Picker for this category (real image binding). */
  onPickImage?: (category: InputAssetCategory, replaceRefId?: string) => void
  /** Legacy text-only add — kept for label-only chips when no picker. */
  onAddLabel?: (category: InputAssetCategory, label: string) => void
  onPreview: (ref: InputAssetRef) => void
  /** VISTOERA_CANVAS_GENERATE_VIDEO_SEMANTIC_DROP_ZONES_EDGE_PORT_ALIGNMENT_AND_COLLAPSIBLE_REFERENCE_ASSETS_P0_V1
   * §九/§十/§十一 — controlled collapse. When provided, the PARENT
   * (`GenerateVideoNode.tsx`) owns the toggle button + the ONE combined
   * "参考资产 N 项" header (which also folds in the Reference Video
   * slot's count, a sibling section this component itself never
   * renders) — this component then renders ONLY its own body,
   * suppressing its built-in `summary`/`inline` header entirely, never
   * a second/duplicate header. `undefined` (the default) preserves the
   * PRE-EXISTING uncontrolled behavior verbatim for every other caller
   * unchanged. */
  collapsed?: boolean
  /** When true, each category row owns its React Flow target handle in DOM. */
  sectionHandles?: boolean
  /** Keyframe scope for structured asset handle ids (`character-in::id`). */
  keyframeEntryId?: string
  /** VistoeraTASK-00064 P1/P4/P6 — restores "drag this card's thumbnail
   * into the Prompt editor → inline Asset Mention chip" for Character/
   * Product/Scene/Other-Reference cards (spec §1/§4), using the EXACT
   * SAME `ASSET_MENTION_DND_TYPE` payload contract `InputMaterialsRow`
   * chips already use — never a second drag contract. A no-op (cards
   * stay non-draggable) whenever omitted, so every non-`generate_video`
   * consumer of this component is unaffected (parity with
   * `InputMaterialsRow`'s own `draggableMentions`). */
  mentionDraggable?: boolean
}

let refSeq = 0
export function genInputAssetRefId(): string {
  refSeq += 1
  return `asset_manual_${Date.now().toString(36)}_${refSeq}`
}

function CategoryAddControls({
  category,
  disabled,
  onPickImage,
  onAddLabel,
  t,
}: {
  category: InputAssetCategory
  disabled?: boolean
  onPickImage?: (c: InputAssetCategory) => void
  onAddLabel?: (c: InputAssetCategory, label: string) => void
  t: (k: string) => string
}) {
  const [openLabel, setOpenLabel] = useState(false)
  const [value, setValue] = useState('')

  return (
    <div className="pcv-video-asset-add-controls nodrag">
      {onPickImage ? (
        <button
          type="button"
          className="pcv-video-asset-add-btn"
          disabled={disabled}
          title={t('generation.inputAssetPickImage')}
          aria-label={t('generation.inputAssetPickImage')}
          onClick={() => onPickImage(category)}
        >
          <PlusOutlined />
        </button>
      ) : null}
      {onAddLabel ? (
        openLabel ? (
          <div className="pcv-video-asset-add-form">
            <input
              autoFocus
              type="text"
              className="pcv-gen-text-input pcv-video-asset-add-input"
              placeholder={t('generation.inputAssetAddPlaceholder')}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && value.trim()) {
                  onAddLabel(category, value.trim())
                  setValue('')
                  setOpenLabel(false)
                }
                if (e.key === 'Escape') setOpenLabel(false)
              }}
              onBlur={() => {
                if (value.trim()) onAddLabel(category, value.trim())
                setValue('')
                setOpenLabel(false)
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            className="pcv-video-asset-add-label-btn"
            disabled={disabled}
            onClick={() => setOpenLabel(true)}
          >
            {t('generation.inputAssetAddLabelOnly')}
          </button>
        )
      ) : null}
    </div>
  )
}

export default function VideoInputAssetsSection({
  workspaceId,
  refs,
  disabled,
  variant = 'summary',
  onRemove,
  onPickImage,
  onAddLabel,
  onPreview,
  collapsed,
  sectionHandles,
  keyframeEntryId,
  mentionDraggable,
}: Props) {
  const { t } = useTranslation('canvas')
  const isControlled = collapsed !== undefined
  const [expanded, setExpanded] = useState(variant === 'inline')
  const showBody = isControlled ? !collapsed : variant === 'inline' || expanded

  const byCategory = new Map<InputAssetCategory, VideoInputAssetDisplayRef[]>()
  for (const r of refs) {
    const list = byCategory.get(r.category) || []
    list.push(r)
    byCategory.set(r.category, list)
  }

  const counts = CATEGORY_ORDER.map((category) => ({
    category,
    count: (byCategory.get(category) || []).length,
  }))
  const total = counts.reduce((sum, c) => sum + c.count, 0)
  const collapsedSummary = counts
    .map(({ category, count }) => `${t(CATEGORY_SHORT_LABEL_KEY[category])} ${count}`)
    .join(' · ')
  const missingImageCount = refs.filter((r) => !r.assetId && !r.generatedOutputId).length

  return (
    <div
      className={`pcv-brief-section pcv-video-input-assets-section${showBody ? ' is-expanded' : ' is-collapsed'}${
        variant === 'inline' ? ' pcv-video-input-assets-section--inline' : ''
      }`}
      data-testid="video-input-assets"
      data-variant={variant}
      data-collapsed={showBody ? 'false' : 'true'}
    >
      {isControlled ? null : variant === 'summary' ? (
        <button
          type="button"
          className="pcv-brief-collapse-toggle pcv-video-input-assets-toggle nodrag"
          data-testid="video-input-assets-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <DownOutlined /> : <RightOutlined />}
          <span className="pcv-video-input-assets-toggle-label">{t('generation.inputAssetsSection')}</span>
          <span className="pcv-brief-section-optional" data-testid="video-input-assets-summary">
            {expanded
              ? t('generation.inputAssetsItemCount', { count: total })
              : t('generation.inputAssetsCollapsedSummary', { count: total, detail: collapsedSummary })}
          </span>
        </button>
      ) : (
        <div className="pcv-video-input-assets-inline-header">
          <span className="pcv-brief-section-label">{t('generation.shotReferenceAssetsSection')}</span>
          <span className="pcv-brief-section-optional">{t('generation.inputAssetsItemCount', { count: total })}</span>
        </div>
      )}

      {missingImageCount > 0 && showBody ? (
        <div className="pcv-gen-capability-warning" data-testid="video-ref-missing-images">
          {t('generation.shotReferenceMissingImages', { count: missingImageCount })}
        </div>
      ) : null}

      {showBody ? (
        <div className="pcv-video-input-assets-body" data-testid="video-input-assets-body">
          {CATEGORY_ORDER.map((category) => {
            const items = byCategory.get(category) || []
            return (
              <div
                key={category}
                className={`pcv-video-asset-category-row pcv-video-input-section${items.length === 0 ? ' is-empty' : ''}`}
                data-testid={`video-input-asset-category-${category}`}
                data-video-input-port={videoStructuredAssetHandleId(category, keyframeEntryId)}
              >
                {sectionHandles ? (
                  <VideoInputSectionHandle
                    id={videoStructuredAssetHandleId(category, keyframeEntryId)}
                    className="pcv-handle pcv-handle--structured-asset"
                    testId={`video-${category === 'object' ? 'product' : category}-in-handle`}
                  />
                ) : null}
                <span className="pcv-video-asset-category-label">
                  {t(CATEGORY_LABEL_KEY[category])}
                  {items.length === 0 ? <span className="pcv-brief-section-optional"> 0</span> : null}
                </span>
                <div className="pcv-video-asset-category-cards nowheel">
                  {items.map((ref) => {
                    const assetArgs = {
                      entityId: ref.assetId,
                      generatedOutputId: ref.generatedOutputId,
                      nodeType: ref.sourceNodeType,
                    }
                    const thumb = ref.assetId || ref.generatedOutputId
                      ? resolveCanvasImageThumbUrl(workspaceId, assetArgs)
                      : null
                    const previewUrl = ref.assetId || ref.generatedOutputId
                      ? resolveCanvasImageHoverPreviewUrl(workspaceId, assetArgs)
                      : null
                    const pending = ref.locked && ref.ready === false
                    const mentionKey = structuredAssetMentionKey(ref.id)
                    const dragEnabled = Boolean(mentionDraggable && ref.label && !pending)
                    const dragProps = dragEnabled
                      ? {
                          ...vistoeraDndProps('asset-mention'),
                          draggable: true,
                          onDragStart: (e: DragEvent) => {
                            const payload: AssetMentionDragPayload = {
                              edgeId: mentionKey,
                              alias: ref.label,
                              kind: 'IMAGE',
                              ...(thumb ? { thumbUrl: thumb } : {}),
                            }
                            e.dataTransfer.setData(ASSET_MENTION_DND_TYPE, JSON.stringify(payload))
                            e.dataTransfer.effectAllowed = 'copy'
                          },
                        }
                      : {}
                    return (
                      <div
                        key={ref.id}
                        className={`pcv-video-asset-card nodrag${ref.assetId || ref.generatedOutputId ? '' : ' is-label-only'}${
                          ref.locked ? ' is-edge-bound' : ''
                        }${pending ? ' is-pending' : ''}${dragEnabled ? ' is-mention-draggable' : ''}`}
                        title={ref.label}
                        data-testid={`video-input-asset-card-${category}`}
                        data-edge-bound={ref.locked ? 'true' : 'false'}
                        data-asset-id={ref.assetId || undefined}
                        onClick={() => onPreview(ref)}
                        {...dragProps}
                      >
                        <span className="pcv-video-asset-card-thumb">
                          {thumb ? (
                            <ImageThumbnailHoverPreview
                              thumbUrl={thumb}
                              previewUrl={previewUrl}
                              alt={ref.label}
                              className="pcv-video-asset-card-thumb-img"
                              disabled={!showBody}
                              focusable
                            />
                          ) : category === 'character' ? (
                            <UserOutlined />
                          ) : (
                            <PictureOutlined />
                          )}
                        </span>
                        <span className="pcv-video-asset-card-label">{ref.label}</span>
                        {onPickImage && !ref.locked ? (
                          <button
                            type="button"
                            className="pcv-video-asset-card-replace"
                            aria-label={t('generation.inputAssetReplaceImage')}
                            onClick={(e) => {
                              e.stopPropagation()
                              onPickImage(category, ref.id)
                            }}
                          >
                            {t('generation.inputAssetReplaceShort')}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="pcv-video-asset-card-remove"
                          aria-label={t('generation.inputAssetRemove')}
                          onClick={(e) => {
                            e.stopPropagation()
                            onRemove(ref.id)
                          }}
                        >
                          <CloseOutlined />
                        </button>
                      </div>
                    )
                  })}
                  <CategoryAddControls
                    category={category}
                    disabled={disabled}
                    onPickImage={onPickImage}
                    onAddLabel={onAddLabel}
                    t={t}
                  />
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
